import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { ILoggedInUserViewModel } from '../model/common.model';
import { IPhoneNumber } from '../model/contact.interface';
import { ICountryList } from '../model/Country.model';
import { CustomerMemberNameListViewModel } from '../model/customerMemberNameList.model';
import { IMemberEventListResponse, INotificationEventList } from '../model/notificationEvent.model';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';
import { IContactPersonMemberIds } from '@interfaces/member-detail.interface';
import { SpecialInstructionCategoryInterface } from '@interfaces/special-instruction-category.interface';
import { DriverList } from './customer.model';
import { IFilterMemberData } from './filterData.model';
import {
  IAssignedRole,
  ICurrentLoationPayLoad,
  IMemberClaims,
  MemberPreferenceSettingsModel,
  MemberViewModel,
  ViewMemberModel,
} from './memeber.model';
import {
  AddContact,
  CreateLocation,
  CustomerList,
  DesignationList,
  DestinationContactNumberList,
  DestinationContactPersonList,
  IEditLocationModel,
} from './service-request';
import { CountryViewModel, ICityViewModel, StateViewModel } from './state.model';
import { IGetLocationViewModel } from './vehicle.model';
import { MemberInterface } from '@interfaces/member.interface';
import { PaginationItems } from '@modules/table-pagination/interfaces/pagination-items.interface';
import { MemberQueryParamsInterface } from '../pages/v2/member/interfaces/member-query-params.interface';

@Injectable({
  providedIn: 'root',
})
export class MemberService {
  // TODO: need to refactor to remove isLoadingSubject$,$memberUpdates, designationList$ and customerList$.
  public isLoadingSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public $memberUpdates: Subject<boolean> = new Subject();
  designationList$: BehaviorSubject<DesignationList[]> = new BehaviorSubject<DesignationList[]>([]);
  customerList$: BehaviorSubject<CustomerList[]> = new BehaviorSubject<CustomerList[]>([]);
  memberDetails$: BehaviorSubject<ILoggedInUserViewModel> = new BehaviorSubject<ILoggedInUserViewModel>(null);

  constructor(private httpClient: HttpClient) {}

  // TODO: need to remove dependency of this method.
  getStateByCountry(countryId: string): Observable<StateViewModel[]> {
    const url = `${environment.apiHost}/State/GetStateByCountryId/${countryId}`;

    return this.httpClient.get<StateViewModel[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getDesignationList(): Observable<DesignationList[]> {
    const url = `${environment.apiUrl}/Contact/GetAllDesignation`;

    return this.httpClient
      .get<DesignationList[]>(url)
      .pipe(
        tap((designationList: DesignationList[]) => {
          // TODO: need to remove dependency of this.
          this.designationList$.next(designationList);
        }),
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllAddressType(): Observable<BaseDropdownInterface[]> {
    const url = `${environment.apiUrl}/Contact/GetAllAddress`;

    return this.httpClient.get<BaseDropdownInterface[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCountryList(): Observable<CountryViewModel[]> {
    const url = `${environment.apiHost}/Country`;

    return this.httpClient.get<CountryViewModel[]>(url).pipe(catchError((error: ICountryList) => throwError(() => error)));
  }

  addContact(contactData: AddContact): Observable<string> {
    const url = `${environment.apiUrl}/Contact/AddContact/`;

    return this.httpClient.post<string>(url, contactData).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addLocation(locationData: CreateLocation): Observable<IGetLocationViewModel> {
    locationData.isGlobal = locationData.isGlobal ?? true;
    const url = `${environment.apiUrl}/ServiceRequest/AddLocation/`;

    return this.httpClient
      .post<IGetLocationViewModel>(url, locationData)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  editLocationName(locationData: IEditLocationModel): Observable<boolean> {
    locationData.isGlobal = locationData.isGlobal ?? true;
    const url = `${environment.apiHost}/Address/EditLocationName`;
    return this.httpClient.put<boolean>(url, locationData).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllCategoryList(): Observable<SpecialInstructionCategoryInterface[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllSpecialInstructionCategoryType`;

    return this.httpClient
      .get<SpecialInstructionCategoryInterface[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getTransportTypeList(): Observable<string[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllTransportType`;
    return this.httpClient.get<string[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getDestinationContactById(destinationId: string): Observable<DestinationContactPersonList[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetDestinationContactById/${destinationId}`;

    return this.httpClient.get<DestinationContactPersonList[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getDestinationContactNumbersById(destinationId: string): Observable<DestinationContactNumberList[]> {
    const url = `${environment.apiUrl}/ServiceRequest/GetAllDestinationPhoneType/${destinationId}`;

    return this.httpClient.get<DestinationContactNumberList[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getCustomerList(): Observable<CustomerList[]> {
    const url = `${environment.apiHost}/Customer/GetAll`;

    return this.httpClient
      .get<CustomerList[]>(url)
      .pipe(
        tap((customerList: CustomerList[]) => {
          // TODO: need to remove dependency of this.
          this.customerList$.next(customerList);
        }),
      )
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getMemberDetailById(memberId: string): Observable<DriverList[]> {
    const url = `${environment.apiUrl}/Contact/GetByMemberId/${memberId}`;

    return this.httpClient.get<DriverList[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getContactListById(customerId: string): Observable<DriverList[]> {
    const url = `${environment.apiUrl}/Contact/GetById/${customerId}`;

    return this.httpClient.get<DriverList[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getPhoneNoListById(memberId: string): Observable<IPhoneNumber[]> {
    const requestUrl = `${environment.apiUrl}/Contact/GetPhoneNumberById/${memberId}`;

    return this.httpClient.get<IPhoneNumber[]>(requestUrl).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilterData(filter: IFilterMemberData, isShowAll: boolean, pageNumber: number, selectedPage: number): Observable<MemberViewModel> {
    const url = `${environment.apiHost}/Member/FilteredMembers/${isShowAll}/${pageNumber}/${selectedPage}`;

    return this.httpClient.post<MemberViewModel>(url, filter).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  addCity(city: ICityViewModel): Observable<string> {
    const url = `${environment.apiHost}/City`;

    return this.httpClient.post<string>(url, city).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  deleteCity(cityId): Observable<boolean> {
    const url = `${environment.apiHost}/City/${cityId}`;

    return this.httpClient.delete<boolean>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getMemberDetails(): Observable<ILoggedInUserViewModel> {
    const url = `${environment.apiHost}/Member/MemberDetails`;

    return this.httpClient.get<ILoggedInUserViewModel>(url).pipe(
      tap((member: ILoggedInUserViewModel) => {
        this.memberDetails$.next(member);
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  GetNotificationSettingByMemberId(memberId): Observable<IMemberEventListResponse> {
    const url = `${environment.apiHost}/Member/GetNotificationSettingByMemberId/${memberId}`;

    return this.httpClient.get<IMemberEventListResponse>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  GetNotificationEventList(memberRole): Observable<INotificationEventList[]> {
    const url = `${environment.apiHost}/Member/GetNotificationEventList/${memberRole}`;

    return this.httpClient.get<INotificationEventList[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  SaveNotificationSetting(values): Observable<boolean> {
    const url = `${environment.apiHost}/Member/SaveNotificationSetting`;

    return this.httpClient.post<boolean>(url, values).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  memberCheckByEmail(email: string): Observable<boolean> {
    const url = `${environment.apiHost}/Member/IsUserExist/${email}`;

    return this.httpClient.get<boolean>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllMemberList(buId: string, isShowAll: boolean, offsetValue: number, limit: number): Observable<MemberViewModel> {
    const url = `${environment.apiHost}/Member/${buId}/${isShowAll}/${offsetValue}/${limit}`;

    return this.httpClient.get<MemberViewModel>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getFilteredMembers(memberQueryParams: MemberQueryParamsInterface): Observable<PaginationItems<MemberInterface>> {
    const url = `${environment.apiHostV2}/members/search`;
    let params = new HttpParams();

    Object.entries(memberQueryParams).forEach(([key, value]: [string, string]): void => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.set(key, value);
      }
    });

    return this.httpClient
      .get<PaginationItems<MemberInterface>>(url, { params })
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  fetchMemberDetailById(memberBusinessUnitId: string): Observable<ViewMemberModel> {
    const url = `${environment.apiHost}/Member/FetchMemberDetail/${memberBusinessUnitId}`;

    return this.httpClient.get<ViewMemberModel>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAllCustomerMemberNameList(customerId: string): Observable<CustomerMemberNameListViewModel[]> {
    const url = `${environment.apiHost}/Member/getAllCustomerMemberNameList/${customerId}`;

    return this.httpClient
      .get<CustomerMemberNameListViewModel[]>(url)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  saveMemberPreferenceSettings(memberId: string, preferenceSettingModel: MemberPreferenceSettingsModel): Observable<boolean> {
    const url = `${environment.apiHost}/Member/MemberPreferenceSettings/${memberId}`;

    return this.httpClient
      .post<boolean>(url, preferenceSettingModel)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  GetMemberPreferenceSettings(memberId: string): Observable<MemberPreferenceSettingsModel> {
    const url = `${environment.apiHost}/Member/GetMemberPreferenceSettings/${memberId}`;

    return this.httpClient.get<MemberPreferenceSettingsModel>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  saveMembersCurrentLocation(payLoad: ICurrentLoationPayLoad): Observable<boolean> {
    const url = `${environment.apiHost}/Member/SaveMembersCurrentLocation`;

    return this.httpClient.post<boolean>(url, payLoad).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getMemberClaimsBasedOnRoleId(roleId: string): Observable<IMemberClaims[]> {
    const url = `${environment.apiHost}/Member/GetMemberClaims/${roleId}`;

    return this.httpClient.get<IMemberClaims[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getAssignedRoleListBasedOnMemberId(memberId: string, businessUnitId: string): Observable<IAssignedRole[]> {
    const url = `${environment.apiHost}/Role/GetAssignedRoleList/${memberId}/${businessUnitId}`;

    return this.httpClient.get<IAssignedRole[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  updateContactPocDetails(serviceRequestId: string, contactPersonMemberIdsUpdate: IContactPersonMemberIds): Observable<boolean> {
    const url = `${environment.apiUrl}/ServiceRequest/UpdateCustomerAndSubCustomerPoc/${serviceRequestId}`;

    return this.httpClient
      .put<boolean>(url, contactPersonMemberIdsUpdate)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getDriverRegions(): Observable<BaseDropdownInterface[]> {
    const url = `${environment.apiHost}/Member/GetDriverRegions`;

    return this.httpClient.get<BaseDropdownInterface[]>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  refreshAdpData(memberId: string): Observable<unknown> {
    const url = `${environment.apiHost}/DriverClock/RefreshAdpData/${memberId}`;

    return this.httpClient.get<unknown>(url).pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }

  getJobTitles(): Observable<string[]> {
    const url = `${environment.apiHost}/Member/job-titles`;

    return this.httpClient.get<string[]>(url);
  }
}
